import { cloneDeep } from 'lodash';
import {
    UPDATE_RULE_SET,
    UPDATE_VALIDATION_RULE,
    UPDATE_BREAK_RULE_SET,
    ADD_ROLLING_PERIOD_RULE,
    REMOVE_ROLLING_PERIOD_RULE_AT_INDEX,
    UPDATE_CURRENT_RULE_SET_IN_LIST,
    UPDATE_CURRENT_AWARDING_RULE,
    UPDATE_CURRENT_CUSTOM_DATA_FIELD_INFOS,
    UPDATE_CURRENT_HYPERFIND_BELONGING_INFO,
    UPDATE_CURRENT_DATAVIEW_INFOS,
    RESET_CURRENT_AWARDING_RULE,
    RESET_RULE_SET,
    RESET_AWARDING_RULE_STORE,
    RESET_VALIDATION_RULE_STORE,
    RESET_BREAK_RULE_STORE,
    ACTION_LOADING
} from './ruleSet.actions';
import {
    AdminSidePanelAction,
    TimeBeforeShiftType,
    MaxConsecutiveDayWeekType,
} from '../../../Enums';

const DEFAULT_AWARDING_RULE_PARAMETER = {
    type: -1,
    order: -1,
    index: -1,
    customDataFieldInfos: null,
    hyperfindBelongingInfos: null,
    dataviewInfos: null,
};
const DEFAULT_VALIDATION_RULES = {
    // 2 Employee rules
    jobTransferValidationType: null,
    isSkills: false,
    isCertifications: false,
    isOnProbation: false,
    probationLength: null,
    probationDataviewId: null,
    probationDataviewKeyAlias: '',
    probationCustomDateFormat: null,
    probationIsAllowToRequestOffers: false,
    isOnRestriction: false,
    restrictionStartAfter: null,
    restrictionDuration: null,
    restrictionDataviewId: null,
    restrictionDataviewKeyAlias: '',
    restrictionCustomDateFormat: null,
    restrictionIsCanMakeRequests: null,
    restrictionIsOnlyManualApproval: false,
    restrictionAllowAdditionalManualApprovalRequests: false,
    restrictionIsAllowRequestingVto: false,
    restrictionMaxNumberOfRequests: null, // 0 is no max
    restrictionLimitedPeriod: null,
    isEmployeeEligibility: false,
    employeeEligibilityLength: null,
    employeeEligibilityPeriod: null,
    employeeEligibilityCustomDays: null,
    employeeEligibilityDataviewId: null,
    employeeEligibilityDataviewKeyAlias: '',
    employeeEligibilityIsUseActualHours: false,
    employeeEligibilityBreakRule: null,
    // 3 Schedule rules
    isMaxHoursPerDay: false,
    isIncludeBreaksInMaxHoursPerDay: false,
    isUseActualHoursInMaxHoursPerDay: false,
    maxHoursPerDayBreakRule: null,
    isMaxHoursPerWeek: false,
    isIncludeBreaksInMaxHoursPerWeek: false,
    isUseActualHoursInMaxHoursPerWeek: false,
    maxHoursPerWeekBreakRule: null,
    isRestTimeBetweenShift: false,
    isUseActualHoursInRestTimeBetweenShift: false,
    isMaxShiftsPerDay: false,
    isMaxHoursPerRollingPeriod: false,
    maxHoursInRollingPeriodRules: [],
    isUseActualHoursInMaxHoursInRollingPeriod: false,
    isIncludeBreaksInMaxHoursInRollingPeriod: false,
    maxHoursInRollingPeriodBreakRule: null,
    isMaxConsecutiveDay: false,
    maxConsecutiveDayWeekType: MaxConsecutiveDayWeekType.RollingWeek,
    isOverrideMaxHoursPerWeek: false,
    isOverrideMaxHoursPerDay: false,
    overrideMaxHoursPerWeekInMinutes: null,
    overrideMaxHoursPerDayInMinutes: null,
    // Maximum bi-weekly hours
    isMaxBiWeeklyHours: false,
    isIncludeBreaksInMaxBiWeeklyHours: false,
    isOverrideBiWeeklyDefaultValue: false,
    overrideBiWeeklyDefaultValueInMinutes: null,
    isOverrideBiWeeklyAnchorDate: false,
    overrideBiWeeklyAnchorDate: null,
    isUseBiWeeklyActualHoursWorked: false,
    maxBiWeeklyHoursBreakRule: null,
    // 4 Shift rules
    isAllowShiftOverlap: false,
    overlapLength: null,
    isValidatePaycodeOverlap: false,
    paycodeOverlapLength: null,
    overlapPaycodeListToValidate: {
        included: [],
        excluded: [],
    },
    isValidateSameDayPaycode: false,
    sameDayPaycodeListToValidate: {
        included: [],
        excluded: [],
    },
    sameDayPaycodeList: [],
    swapPeriodType: null,
    isOnlySwapOnSameLength: false,
    isTimeBeforeOrAfterShiftStartAllowed: false,
    timeBeforeShiftLength: 0,
    timeBeforeShiftType: TimeBeforeShiftType.Hours,
    isTimeAfterShift: false,
    isMultipleShiftOnSameDayRuleEnabled: false,
    maxMultipleShiftConsecutiveDay: null,
    maxMultipleShiftPerWeek: null,
}
const DEFAULT_BREAK_RULE_PARAMETER = {
    condition: -1,
    shiftDuration: 0.0,
    action: -1,
    actionDuration: 0.0,
};

/** @type {RuleSet.IRuleSetDetails} */
const initialState = {
    ruleSetId: -1,
    name: '',
    description: '',
    isActive: true,
    settingNamesUsingRule: [],
    isEditable: false,
    isCustomRule: false,
    ruleSetType: -1,
    requestType: null,
    requestSubtype: null,
    awardingRules: [],
    validationRuleSet: cloneDeep(DEFAULT_VALIDATION_RULES),
    adminSidePanelAction: AdminSidePanelAction.None,
    currentAwardingRule: cloneDeep(DEFAULT_AWARDING_RULE_PARAMETER),
    breakRules: cloneDeep(DEFAULT_BREAK_RULE_PARAMETER)
};

/**
 * @param {RuleSet.IRuleSetDetails} state
 * @param {EmploymentBillboard.IAction} action
 * */
export default function adminAwardingRuleReducer(state = initialState, action) {
    const { type, payload } = action;

    switch (type) {
        case UPDATE_RULE_SET:
            return {
                ...state,
                ...payload,
            };
        case UPDATE_VALIDATION_RULE:
            return {
                ...state,
                validationRuleSet: {
                    ...state.validationRuleSet,
                    ...payload,
                },
            };
        case UPDATE_BREAK_RULE_SET:
            return {
                ...state,
                ...payload,
            };
        case ADD_ROLLING_PERIOD_RULE: {
            return {
                ...state,
                validationRuleSet: {
                    ...state.validationRuleSet,
                    maxHoursInRollingPeriodRules: [...state.validationRuleSet.maxHoursInRollingPeriodRules, payload],
                }
            }
        }
        case REMOVE_ROLLING_PERIOD_RULE_AT_INDEX: {
            state.validationRuleSet.maxHoursInRollingPeriodRules.splice(payload, 1);
            return {
                ...state,
                validationRuleSet: {
                    ...state.validationRuleSet,
                }
            }
        }
        case UPDATE_CURRENT_RULE_SET_IN_LIST: {
            const { currentAwardingRule, index } = payload;
            if (index === -1) {
                return {
                    ...state,
                    currentAwardingRule: currentAwardingRule,
                };
            }
            else {
                state.awardingRules[index] = { ...cloneDeep(currentAwardingRule) }
                return {
                    ...state,
                    currentAwardingRule: currentAwardingRule,
                    awardingRules: state.awardingRules,
                };
            }

        }
        case UPDATE_CURRENT_AWARDING_RULE: {
            return {
                ...state,
                currentAwardingRule: {
                    ...state.currentAwardingRule,
                    ...payload,
                },
            };
        }
        case UPDATE_CURRENT_CUSTOM_DATA_FIELD_INFOS: {
            return {
                ...state,
                currentAwardingRule: {
                    ...state.currentAwardingRule,
                    customDataFieldInfos: {
                        ...state.currentAwardingRule.customDataFieldInfos,
                        ...payload,
                    }
                },
            };
        }
        case UPDATE_CURRENT_HYPERFIND_BELONGING_INFO: {
            return {
                ...state,
                currentAwardingRule: {
                    ...state.currentAwardingRule,
                    hyperfindBelongingInfos: {
                        ...state.currentAwardingRule.hyperfindBelongingInfos,
                        ...payload,
                    }
                },
            };
        }
        case UPDATE_CURRENT_DATAVIEW_INFOS: {
            return {
                ...state,
                currentAwardingRule: {
                    ...state.currentAwardingRule,
                    dataviewInfos: {
                        ...state.currentAwardingRule.dataviewInfos,
                        ...payload,
                    }
                },
            };
        }
        case RESET_CURRENT_AWARDING_RULE: {
            return {
                ...state,
                currentAwardingRule: cloneDeep(DEFAULT_AWARDING_RULE_PARAMETER),
            };
        }
        case RESET_AWARDING_RULE_STORE: {
            return {
                ...state,
                currentAwardingRule: cloneDeep(DEFAULT_AWARDING_RULE_PARAMETER),
                awardingRules: [],
                validationRuleSet: null,
                breakRules: null
            };
        }
        case RESET_VALIDATION_RULE_STORE: {
            var validationRuleSet = cloneDeep(DEFAULT_VALIDATION_RULES);
            validationRuleSet.overlapPaycodeListToValidate.excluded = [...payload.paycodeList];
            validationRuleSet.overlapPaycodeListToValidate.included = [];
            validationRuleSet.sameDayPaycodeListToValidate.excluded = [...payload.paycodeList];
            validationRuleSet.sameDayPaycodeListToValidate.included = [];
            return {
                ...state,
                requestType: payload.isResetRequestType ? null : state.requestType,
                requestSubtype: payload.isResetRequestSubtype ? null : state.requestSubtype,
                currentAwardingRule: null,
                awardingRules: null,
                requestSubtype: null,
                validationRuleSet: validationRuleSet,
                breakRules: null,
            };
        }
        case RESET_BREAK_RULE_STORE: {
            return {
                ...state,
                currentAwardingRule: null,
                awardingRules: null,
                validationRuleSet: null,
                breakRules: []
            };
        }
        case RESET_RULE_SET:
            return {
                ...initialState,
            };
        case ACTION_LOADING:
            return {
                ...state,
                isActionLoading: payload
            };
        default:
            return state;
    }
}
